import type {
  CustomerCategoryRecord,
  LeadActivityRecord,
  LeadCollectionRecord,
  LeadDetailRecord,
  LeadRecord,
  LeadStatus
} from '@shared/types';
import { normalizeBusinessIconName, type BusinessIconName } from '@renderer/shared/components/icons';
import type { SearchableSelectOption } from '@renderer/shared/components/primitives';
import type { LeadsCityFilter, LeadsDueFilter, LeadsSort } from './leads-view-store';

type BadgeTone = 'neutral' | 'primary' | 'success' | 'warning' | 'info' | 'dark';
type SortDirection = 'asc' | 'desc';

export interface LeadRowViewModel {
  id: string;
  name: string;
  companyLabel: string;
  contactLabel: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  city: string | null;
  sourceLabel: string;
  categoryLabel: string;
  categoryIconName: BusinessIconName;
  categoryArchived: boolean;
  status: LeadStatus;
  statusLabel: string;
  statusTone: BadgeTone;
  nextContactLabel: string;
  nextContactTone: BadgeTone;
  createdAtLabel: string;
  lastActivityLabel: string;
  tags: string[];
  customerName: string | null;
  projectName: string | null;
}

export interface LeadSelectionItemViewModel {
  id: string;
  label: string;
  subtitle: string | null;
}

export interface LeadPlannerItemViewModel {
  id: string;
  name: string;
  companyLabel: string;
  contactLabel: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  city: string | null;
  categoryLabel: string;
  categoryIconName: BusinessIconName;
  status: LeadStatus;
  statusLabel: string;
  statusTone: BadgeTone;
  lastActivityLabel: string;
  contactedOnDay: boolean;
  tags: string[];
}

export interface LeadPlannerGroupViewModel {
  id: string;
  label: string;
  cityLabel: string;
  categoryLabel: string;
  categoryIconName: BusinessIconName;
  items: LeadPlannerItemViewModel[];
}

export interface LeadPlannerDayViewModel {
  dateKey: string;
  isoDate: string;
  label: string;
  shortLabel: string;
  weekdayLabel: string;
  isToday: boolean;
  isPast: boolean;
  isSelected: boolean;
  plannedCount: number;
  contactedCount: number;
  groups: LeadPlannerGroupViewModel[];
}

export interface LeadEditorViewModel {
  title: string;
  subtitle: string;
  statusLabel: string;
  statusTone: BadgeTone;
  phone: string | null;
  email: string | null;
  website: string | null;
  categoryLabel: string;
  categoryIconName: BusinessIconName;
  categoryArchived: boolean;
  nextContactLabel: string;
  lastActivityLabel: string;
  customerName: string | null;
  projectName: string | null;
  activities: Array<{
    id: string;
    title: string;
    meta: string;
    comment: string | null;
  }>;
}

export function getLeadStatusTone(status: LeadStatus): BadgeTone {
  switch (status) {
    case 'new':
    case 'to_call':
      return 'primary';
    case 'contacted':
      return 'info';
    case 'follow_up':
      return 'warning';
    case 'qualified':
    case 'converted':
      return 'success';
    case 'lost':
      return 'neutral';
  }
}

function isClosedLead(lead: LeadRecord): boolean {
  return lead.status === 'converted' || lead.status === 'lost';
}

function getCustomerCategory(categories: CustomerCategoryRecord[], customerCategoryId: string | null): CustomerCategoryRecord | null {
  return customerCategoryId ? categories.find((category) => category.id === customerCategoryId) ?? null : null;
}

function isDue(lead: LeadRecord, now: Date): boolean {
  return !isClosedLead(lead) && lead.nextContactAt !== null && new Date(lead.nextContactAt).getTime() <= now.getTime();
}

function isScheduled(lead: LeadRecord, now: Date): boolean {
  return !isClosedLead(lead) && lead.nextContactAt !== null && new Date(lead.nextContactAt).getTime() > now.getTime();
}

function normalizeFilterText(value: string | null | undefined): string | null {
  const normalized = value?.trim();
  return normalized ? normalized.toLocaleLowerCase('ro-RO') : null;
}

function getDateKey(value: string | null | undefined): string | null {
  return value ? value.slice(0, 10) : null;
}

function toPlannerIsoDate(dateKey: string): string {
  return `${dateKey}T12:00:00.000Z`;
}

function createPlannerWeekDateKeys(weekStartDate: string): string[] {
  const dates: string[] = [];
  const cursor = new Date(toPlannerIsoDate(weekStartDate));
  cursor.setUTCHours(12, 0, 0, 0);

  while (dates.length < 5) {
    const day = cursor.getUTCDay();
    if (day !== 0 && day !== 6) {
      dates.push(cursor.toISOString().slice(0, 10));
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return dates;
}

function matchesCityFilter(lead: LeadRecord, cityFilter: LeadsCityFilter): boolean {
  if (cityFilter === 'all') {
    return true;
  }

  const normalizedCity = normalizeFilterText(lead.city);
  if (cityFilter === 'missing') {
    return normalizedCity === null;
  }

  return normalizedCity === normalizeFilterText(cityFilter);
}

function matchesCategoryFilter(lead: LeadRecord, categoryFilter: 'all' | 'unassigned' | string): boolean {
  return (
    categoryFilter === 'all' ||
    (categoryFilter === 'unassigned' && !lead.customerCategoryId) ||
    lead.customerCategoryId === categoryFilter
  );
}

function matchesLeadFilters(
  lead: LeadRecord,
  options: {
    statusFilter: 'all' | LeadStatus;
    categoryFilter: 'all' | 'unassigned' | string;
    cityFilter: LeadsCityFilter;
  }
): boolean {
  return (
    (options.statusFilter === 'all' || lead.status === options.statusFilter) &&
    matchesCategoryFilter(lead, options.categoryFilter) &&
    matchesCityFilter(lead, options.cityFilter)
  );
}

function formatDateTime(locale: string, value: string | null): string | null {
  if (!value) {
    return null;
  }

  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
}

function capitalize(value: string): string {
  return value.length > 0 ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;
}

function formatCreatedAtDateTime(locale: string, value: string): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  if (locale === 'ro-RO') {
    const parts = new Intl.DateTimeFormat('ro-RO', {
      day: 'numeric',
      hour: '2-digit',
      hour12: false,
      minute: '2-digit',
      month: 'long',
      year: 'numeric'
    }).formatToParts(date);
    const getPart = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? '';

    return `${getPart('day')} ${capitalize(getPart('month'))} ${getPart('year')} ${getPart('hour')}:${getPart('minute')}`;
  }

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    month: 'long',
    year: 'numeric'
  })
    .format(date)
    .replace(',', '');
}

function formatRelativeContact(lead: LeadRecord, locale: string, t: (key: string, values?: Record<string, string | number>) => string): {
  label: string;
  tone: BadgeTone;
} {
  if (!lead.nextContactAt || isClosedLead(lead)) {
    return { label: t('common.notSet'), tone: 'neutral' };
  }

  const now = new Date();
  const contactDate = new Date(lead.nextContactAt);
  const formatted = formatDateTime(locale, lead.nextContactAt) ?? t('common.notSet');

  if (contactDate.getTime() <= now.getTime()) {
    return { label: t('leads.card.dueNow', { date: formatted }), tone: 'warning' };
  }

  return { label: t('leads.card.nextContact', { date: formatted }), tone: 'primary' };
}

function withDirection(value: number, direction: SortDirection): number {
  return direction === 'asc' ? value : value * -1;
}

const leadStatusSortRank: Record<LeadStatus, number> = {
  new: 0,
  to_call: 1,
  contacted: 2,
  follow_up: 3,
  qualified: 4,
  converted: 5,
  lost: 6
};

function compareNullableText(left: string | null | undefined, right: string | null | undefined): number {
  const normalizedLeft = left?.trim() ?? '';
  const normalizedRight = right?.trim() ?? '';

  if (!normalizedLeft && !normalizedRight) {
    return 0;
  }

  if (!normalizedLeft) {
    return 1;
  }

  if (!normalizedRight) {
    return -1;
  }

  return normalizedLeft.localeCompare(normalizedRight);
}

function compareDateValue(left: string | null | undefined, right: string | null | undefined): number {
  const leftTime = left ? new Date(left).getTime() : Number.NaN;
  const rightTime = right ? new Date(right).getTime() : Number.NaN;
  const leftHasDate = Number.isFinite(leftTime);
  const rightHasDate = Number.isFinite(rightTime);

  if (!leftHasDate && !rightHasDate) {
    return 0;
  }

  if (!leftHasDate) {
    return 1;
  }

  if (!rightHasDate) {
    return -1;
  }

  return leftTime - rightTime;
}

function getSortableContactValue(lead: LeadRecord): string | null {
  return lead.contactName ?? lead.phone ?? lead.email;
}

function getSortableCategoryName(categories: CustomerCategoryRecord[], lead: LeadRecord): string | null {
  return getCustomerCategory(categories, lead.customerCategoryId)?.name ?? null;
}

function sortLeads(leads: LeadRecord[], categories: CustomerCategoryRecord[], sort: LeadsSort, direction: SortDirection): LeadRecord[] {
  const nextLeads = [...leads];

  return nextLeads.sort((left, right) => {
    switch (sort) {
      case 'name':
        return withDirection(left.name.localeCompare(right.name), direction);
      case 'contact':
        return withDirection(compareNullableText(getSortableContactValue(left), getSortableContactValue(right)), direction);
      case 'category':
        return withDirection(compareNullableText(getSortableCategoryName(categories, left), getSortableCategoryName(categories, right)), direction);
      case 'recent':
        return withDirection(compareDateValue(left.createdAt, right.createdAt), direction);
      case 'status':
        return withDirection(leadStatusSortRank[left.status] - leadStatusSortRank[right.status], direction);
      case 'website':
        return withDirection(compareNullableText(left.currentWebsiteUrl, right.currentWebsiteUrl), direction);
      case 'city':
        return withDirection(compareNullableText(left.city, right.city), direction);
      case 'nextContact':
        return withDirection(compareDateValue(left.nextContactAt, right.nextContactAt), direction);
    }
  });
}

export function selectLeadsSummary(collection: LeadCollectionRecord) {
  return {
    activeCount: collection.leads.filter((lead) => !isClosedLead(lead)).length,
    convertedCount: collection.leads.filter((lead) => lead.status === 'converted').length,
    dueCount: collection.leads.filter((lead) => isDue(lead, new Date())).length
  };
}

export function selectLeadCategoryOptions(
  collection: LeadCollectionRecord,
  t: (key: string) => string
): SearchableSelectOption[] {
  return [
    { value: 'all', label: t('leads.filters.allCategories'), icon: 'filter' },
    { value: 'unassigned', label: t('leads.filters.unassignedCategory'), icon: 'times' },
    ...collection.customerCategories.map((category) => ({
      value: category.id,
      label: category.name,
      icon: normalizeBusinessIconName(category.iconName),
      description: category.archivedAt ? t('common.archived') : undefined
    }))
  ];
}

export function selectLeadCityOptions(collection: LeadCollectionRecord, t: (key: string) => string): SearchableSelectOption[] {
  const cities = [
    ...new Set(
      collection.leads
        .map((lead) => lead.city?.trim() ?? '')
        .filter(Boolean)
    )
  ].sort((left, right) => left.localeCompare(right, 'ro-RO'));

  return [
    { value: 'all', label: t('leads.filters.allCities'), icon: 'location' },
    { value: 'missing', label: t('leads.filters.missingCity'), icon: 'times' },
    ...cities.map((city) => ({
      value: city,
      label: city,
      icon: 'location' as const
    }))
  ];
}

function createPlannerItem(
  collection: LeadCollectionRecord,
  lead: LeadRecord,
  dateKey: string,
  options: {
    locale: string;
    t: (key: string, values?: Record<string, string | number>) => string;
  }
): LeadPlannerItemViewModel {
  const category = getCustomerCategory(collection.customerCategories, lead.customerCategoryId);

  return {
    id: lead.id,
    name: lead.name,
    companyLabel: lead.companyName ?? options.t('common.notSet'),
    contactLabel: lead.contactName ?? options.t('common.notSet'),
    phone: lead.phone,
    email: lead.email,
    website: lead.currentWebsiteUrl,
    city: lead.city,
    categoryLabel: category?.name ?? options.t('leads.filters.unassignedCategory'),
    categoryIconName: normalizeBusinessIconName(category?.iconName),
    status: lead.status,
    statusLabel: options.t(`enums.leadStatus.${lead.status}`),
    statusTone: getLeadStatusTone(lead.status),
    lastActivityLabel: formatDateTime(options.locale, lead.lastActivityAt) ?? options.t('leads.card.noActivity'),
    contactedOnDay: getDateKey(lead.lastActivityAt) === dateKey,
    tags: lead.tags
  };
}

function formatPlannerDayLabel(locale: string, dateKey: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long'
  }).format(new Date(toPlannerIsoDate(dateKey)));
}

function formatPlannerShortDayLabel(locale: string, dateKey: string): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short'
  }).format(new Date(toPlannerIsoDate(dateKey)));
}

function formatPlannerWeekdayLabel(locale: string, dateKey: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short'
  }).format(new Date(toPlannerIsoDate(dateKey)));
}

export function selectLeadPlannerWeek(
  collection: LeadCollectionRecord,
  options: {
    locale: string;
    weekStartDate: string;
    selectedDate: string;
    statusFilter: 'all' | LeadStatus;
    categoryFilter: 'all' | 'unassigned' | string;
    cityFilter: LeadsCityFilter;
    t: (key: string, values?: Record<string, string | number>) => string;
  }
): LeadPlannerDayViewModel[] {
  const todayKey = new Date().toISOString().slice(0, 10);
  const selectedDateKey = options.selectedDate;
  const dateKeys = createPlannerWeekDateKeys(options.weekStartDate);
  const scheduledLeads = collection.leads
    .filter((lead) => {
      const dateKey = getDateKey(lead.nextContactAt);
      return Boolean(dateKey && dateKeys.includes(dateKey) && !isClosedLead(lead) && matchesLeadFilters(lead, options));
    })
    .sort((left, right) => {
      const dateDelta = compareDateValue(left.nextContactAt, right.nextContactAt);
      if (dateDelta !== 0) {
        return dateDelta;
      }

      const cityDelta = compareNullableText(left.city, right.city);
      if (cityDelta !== 0) {
        return cityDelta;
      }

      const leftCategory = getSortableCategoryName(collection.customerCategories, left);
      const rightCategory = getSortableCategoryName(collection.customerCategories, right);
      const categoryDelta = compareNullableText(leftCategory, rightCategory);
      if (categoryDelta !== 0) {
        return categoryDelta;
      }

      return left.name.localeCompare(right.name);
    });

  return dateKeys.map((dateKey) => {
    const itemsForDay = scheduledLeads.filter((lead) => getDateKey(lead.nextContactAt) === dateKey);
    const groupsById = new Map<string, LeadPlannerGroupViewModel>();

    for (const lead of itemsForDay) {
      const item = createPlannerItem(collection, lead, dateKey, options);
      const cityLabel = lead.city ?? options.t('leads.planner.noCity');
      const category = getCustomerCategory(collection.customerCategories, lead.customerCategoryId);
      const categoryLabel = category?.name ?? options.t('leads.filters.unassignedCategory');
      const groupId = `${normalizeFilterText(cityLabel) ?? 'missing-city'}::${lead.customerCategoryId ?? 'unassigned'}`;
      const currentGroup =
        groupsById.get(groupId) ??
        ({
          id: groupId,
          label: options.t('leads.planner.groupLabel', {
            city: cityLabel,
            category: categoryLabel
          }),
          cityLabel,
          categoryLabel,
          categoryIconName: normalizeBusinessIconName(category?.iconName),
          items: []
        } satisfies LeadPlannerGroupViewModel);

      currentGroup.items.push(item);
      groupsById.set(groupId, currentGroup);
    }

    const groups = [...groupsById.values()].sort((left, right) => {
      const cityDelta = left.cityLabel.localeCompare(right.cityLabel, 'ro-RO');
      return cityDelta !== 0 ? cityDelta : left.categoryLabel.localeCompare(right.categoryLabel, 'ro-RO');
    });

    return {
      dateKey,
      isoDate: toPlannerIsoDate(dateKey),
      label: formatPlannerDayLabel(options.locale, dateKey),
      shortLabel: formatPlannerShortDayLabel(options.locale, dateKey),
      weekdayLabel: formatPlannerWeekdayLabel(options.locale, dateKey),
      isToday: dateKey === todayKey,
      isPast: dateKey < todayKey,
      isSelected: dateKey === selectedDateKey,
      plannedCount: groups.reduce((total, group) => total + group.items.length, 0),
      contactedCount: groups.reduce((total, group) => total + group.items.filter((item) => item.contactedOnDay).length, 0),
      groups
    };
  });
}

export function selectLeadRows(
  collection: LeadCollectionRecord,
  options: {
    locale: string;
    statusFilter: 'all' | LeadStatus;
    categoryFilter: 'all' | 'unassigned' | string;
    cityFilter: LeadsCityFilter;
    dueFilter: LeadsDueFilter;
    sort: LeadsSort;
    sortDirection?: SortDirection | undefined;
    t: (key: string, values?: Record<string, string | number>) => string;
  }
): LeadRowViewModel[] {
  const now = new Date();

  return sortLeads(
    collection.leads.filter((lead) => {
      const matchesDue =
        options.dueFilter === 'all' ||
        (options.dueFilter === 'due' && isDue(lead, now)) ||
        (options.dueFilter === 'scheduled' && isScheduled(lead, now)) ||
        (options.dueFilter === 'none' && !lead.nextContactAt);

      return matchesLeadFilters(lead, options) && matchesDue;
    }),
    collection.customerCategories,
    options.sort,
    options.sortDirection ?? (options.sort === 'recent' ? 'desc' : 'asc')
  ).map((lead) => {
    const nextContact = formatRelativeContact(lead, options.locale, options.t);
    const customer = lead.customerId ? collection.customerSummaries.find((entry) => entry.id === lead.customerId) ?? null : null;
    const project = lead.projectId ? collection.projectSummaries.find((entry) => entry.id === lead.projectId) ?? null : null;
    const category = getCustomerCategory(collection.customerCategories, lead.customerCategoryId);

    return {
      id: lead.id,
      name: lead.name,
      companyLabel: lead.companyName ?? options.t('common.notSet'),
      contactLabel: lead.contactName ?? options.t('common.notSet'),
      phone: lead.phone,
      email: lead.email,
      website: lead.currentWebsiteUrl,
      city: lead.city,
      sourceLabel: lead.source ?? options.t('common.notSet'),
      categoryLabel: category?.name ?? options.t('leads.filters.unassignedCategory'),
      categoryIconName: normalizeBusinessIconName(category?.iconName),
      categoryArchived: Boolean(category?.archivedAt),
      status: lead.status,
      statusLabel: options.t(`enums.leadStatus.${lead.status}`),
      statusTone: getLeadStatusTone(lead.status),
      nextContactLabel: nextContact.label,
      nextContactTone: nextContact.tone,
      createdAtLabel: formatCreatedAtDateTime(options.locale, lead.createdAt),
      lastActivityLabel: formatDateTime(options.locale, lead.lastActivityAt) ?? options.t('leads.card.noActivity'),
      tags: lead.tags,
      customerName: customer?.name ?? null,
      projectName: project?.name ?? null
    };
  });
}

export function selectLeadSelectionItems(collection: LeadCollectionRecord): LeadSelectionItemViewModel[] {
  return collection.leads.map((lead) => ({
    id: lead.id,
    label: lead.name,
    subtitle: lead.companyName ?? lead.city ?? lead.currentWebsiteUrl
  }));
}

export function selectLeadEditorModel(
  detail: LeadDetailRecord,
  options: {
    locale: string;
    t: (key: string, values?: Record<string, string | number>) => string;
  }
): LeadEditorViewModel {
  const lead = detail.lead;
  const customer = lead.customerId ? detail.customerSummaries.find((entry) => entry.id === lead.customerId) ?? null : null;
  const project = lead.projectId ? detail.projectSummaries.find((entry) => entry.id === lead.projectId) ?? null : null;
  const category = getCustomerCategory(detail.customerCategories, lead.customerCategoryId);
  const nextContact = formatRelativeContact(lead, options.locale, options.t);

  function activityTitle(activity: LeadActivityRecord): string {
    return `${options.t(`enums.leadActivityKind.${activity.kind}`)} / ${options.t(`enums.leadActivityOutcome.${activity.outcome}`)}`;
  }

  return {
    title: lead.name,
    subtitle: lead.companyName ?? lead.contactName ?? lead.city ?? options.t('leads.editor.subtitleFallback'),
    statusLabel: options.t(`enums.leadStatus.${lead.status}`),
    statusTone: getLeadStatusTone(lead.status),
    phone: lead.phone,
    email: lead.email,
    website: lead.currentWebsiteUrl,
    categoryLabel: category?.name ?? options.t('leads.filters.unassignedCategory'),
    categoryIconName: normalizeBusinessIconName(category?.iconName),
    categoryArchived: Boolean(category?.archivedAt),
    nextContactLabel: nextContact.label,
    lastActivityLabel: formatDateTime(options.locale, lead.lastActivityAt) ?? options.t('leads.card.noActivity'),
    customerName: customer?.name ?? null,
    projectName: project?.name ?? null,
    activities: detail.activities.map((activity) => ({
      id: activity.id,
      title: activityTitle(activity),
      meta: formatDateTime(options.locale, activity.occurredAt) ?? options.t('common.notSet'),
      comment: activity.comment
    }))
  };
}
